import {H5ForecastListing, H5WeatherSource, listingKey} from 'src/lib/api/h5';

// Model names stay in English in every locale until the hydrology terms (lumped, distributed, …)
// have reviewed translations.

const MODEL_TYPE_NAMES: Record<string, string | undefined> = {
  omni: 'HF',
  tirex: 'TiRex',
  ensemble: 'Ensemble',
  // Named by source, as the legacy dashboard names its NWPS-sourced RFC series.
  'rfc-nwps': 'NOAA RFC',
};

// How a model is built, by the hf_models TfModel class in its tf_model slot.
const MODEL_KINDS: Record<string, string | undefined> = {
  OmniRoutedTfModel: 'Routed',
  OmniDistributedTfModel: 'Distributed',
  OmniEnsembleTfModel: 'Ensemble',
  OmniHierarchicalTfModel: 'Hierarchical',
  OmniLumpedTfModel: 'Lumped',
};

const WEATHER_SOURCE_NAMES: Record<H5WeatherSource, string> = {
  gefs: 'GEFS',
  'era5-land': 'ERA5-Land',
  era5: 'ERA5',
  gfs: 'GFS',
  hrrr: 'HRRR',
  hrdps: 'HRDPS',
  'ifs-ens': 'IFS ENS',
  imerg: 'IMERG',
  mrms: 'MRMS',
};

const ENSEMBLING_METHODS: Record<string, string | undefined> = {
  CombinedTracesEnsemblingMethod: 'combined traces',
};

type AvailableListing = Extract<H5ForecastListing, {status: 'available'}>;
type NamedListing = Pick<H5ForecastListing, 'modelType' | 'modelId' | 'visibility'> &
  Partial<Pick<AvailableListing, 'assimilation' | 'modelConfigSignature' | 'weatherSources'>>;

/**
 * The name a model goes by on the chart. Models at one site share its customer and site, so a
 * model is named by what tells it apart: its kind, the weather it runs on, and whether it
 * assimilates, read from the config signature, weather sources and assimilation settings the
 * listing carries.
 */
export function modelDisplayName(listing: NamedListing): string {
  if (listing.visibility === 'primary') return 'HydroForecast';
  const typeName = MODEL_TYPE_NAMES[listing.modelType] ?? listing.modelType;
  const components = listing.modelConfigSignature?.components;
  if (!components) return `${typeName} ${listing.modelId}`;
  const kind = MODEL_KINDS[components.tf_model ?? ''];
  // A source a newer backend adds is shown by its ID until it has a name here.
  const weather = (listing.weatherSources ?? []).map(
    (source) => WEATHER_SOURCE_NAMES[source] ?? source
  );
  const details = [
    weather.join(' + '),
    ENSEMBLING_METHODS[components.ensembling_method ?? ''],
    listing.assimilation && 'assimilated',
  ].filter(Boolean);
  return [kind ? `${typeName} ${kind}` : typeName, ...details].join(' · ');
}

const renameShared = (names: string[], rename: (index: number) => string) =>
  names.map((name, index) =>
    names.filter((other) => other === name).length > 1 ? rename(index) : name
  );

/**
 * The names of the models a section lists, no two alike. Models that read the same get their ID
 * added, so each still has a name of its own on the chart and in the picker. The legend groups a
 * model's lines and bands by its name.
 */
export function modelDisplayNames(listings: NamedListing[]): string[] {
  const names = listings.map(modelDisplayName);
  const withIds = renameShared(names, (index) => `${names[index]} · ${listings[index].modelId}`);
  // An ID can repeat under another model type, or an added ID can spell another model's name.
  return renameShared(withIds, (index) => `${names[index]} · ${listingKey(listings[index])}`);
}
